import { useConfigStore } from '#stores/config.store';
import { useUserStore } from '#stores/user.store';
import { FILTER_NAME_TO_KPI_FILTER, getFilterTypeAndKeyFromNodeId } from '@carrefour-gcs/shared';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useGetFilters } from './useGetFilters';
import { useNormalizeSegmentMicroFilters } from './useNormalizeSegmentMicroFilters';

export const useGetFiltersForKpis = () => {
  const filters = useGetFilters();
  const { authenticatedUser } = useUserStore();
  const isInternalOrganization = authenticatedUser?.organization?.name.toLocaleLowerCase().includes('carrefour');

  const { customerSegmentation } = storeToRefs(useConfigStore());

  const { normalizedConsostyleMicroFilters, normalizedLifestageMicroFilters } = useNormalizeSegmentMicroFilters({
    consostyleMicroFilters: computed(() => filters.value.consostyleMicroFilters),
    lifestageMicroFilters: computed(() => filters.value.lifestageMicroFilters)
  });

  return computed(() => {
    const viewConfig = customerSegmentation.value;

    return {
      [FILTER_NAME_TO_KPI_FILTER['store']]: filters.value.storeFilters,
      [FILTER_NAME_TO_KPI_FILTER['region']]: filters.value.storesRegionFilters,
      [FILTER_NAME_TO_KPI_FILTER['sector']]: filters.value.sectorFilters,
      [FILTER_NAME_TO_KPI_FILTER['department']]: filters.value.departmentFilters,
      [FILTER_NAME_TO_KPI_FILTER['grpClass']]: filters.value.groupClassFilters,
      [FILTER_NAME_TO_KPI_FILTER['class']]: filters.value.classFilters,
      [FILTER_NAME_TO_KPI_FILTER['subClass']]: filters.value.subClassFilters,
      [FILTER_NAME_TO_KPI_FILTER['activityType']]: filters.value.activityTypeFilters,
      [FILTER_NAME_TO_KPI_FILTER['chainType']]: filters.value.chainTypeFilters,
      [FILTER_NAME_TO_KPI_FILTER['integratedFranchised']]: filters.value.integratedFranchisedFilters,
      [FILTER_NAME_TO_KPI_FILTER['brandType']]: filters.value.triptyqueFilters,
      [FILTER_NAME_TO_KPI_FILTER['bemMainHolding']]: filters.value.bemHoldingFilters,
      [FILTER_NAME_TO_KPI_FILTER['bemMerchandise']]: filters.value.bemMerchandiseFilters,
      [FILTER_NAME_TO_KPI_FILTER['subSupplier']]: filters.value.subSupplierFilters,
      [FILTER_NAME_TO_KPI_FILTER['localSupplier']]: filters.value.localSupplierFilters,
      [FILTER_NAME_TO_KPI_FILTER['brand']]: filters.value.brandFilters,
      [FILTER_NAME_TO_KPI_FILTER['competitorBrand']]: filters.value.competitorBrandsFilters,
      [FILTER_NAME_TO_KPI_FILTER['brandDesc']]: filters.value.brandDescFilters,
      [FILTER_NAME_TO_KPI_FILTER['item']]: filters.value.itemFilters,
      [FILTER_NAME_TO_KPI_FILTER['barcode']]: filters.value.barcodeFilters,
      [FILTER_NAME_TO_KPI_FILTER['competitorBaseBarcode']]: [
        ...filters.value.competitorBaseBarcodeFilters,
        ...filters.value.competitorComparisonBarcodeFilters
      ], // The values of both filters, competitorBaseBarcode and competitorComparisonBarcode, need to be merged into barcode_anonymized.
      [FILTER_NAME_TO_KPI_FILTER['competitorBaseItem']]: [
        ...filters.value.competitorBaseItemFilters,
        ...filters.value.competitorComparisonItemFilters
      ],
      [FILTER_NAME_TO_KPI_FILTER['assortmentTypology']]: filters.value.assortmentTypologyFilters,
      [FILTER_NAME_TO_KPI_FILTER['productTypology']]: filters.value.productTypologyFilters,
      [FILTER_NAME_TO_KPI_FILTER['competitor']]: filters.value.competitorFilters.flatMap((nodeId) => {
        const { filterKey } = getFilterTypeAndKeyFromNodeId(nodeId);
        return filterKey.split(',').map((k) => `competitor=${k.trim()}`);
      }),
      [FILTER_NAME_TO_KPI_FILTER['customerSegmentationViewType']]: ['customerSegmentationViewType=segmentation'],
      [FILTER_NAME_TO_KPI_FILTER['customerSegmentationType']]: [`customerSegmentationType=${viewConfig.type}`],
      [FILTER_NAME_TO_KPI_FILTER['customerSegmentationGranularity']]: [
        `customerSegmentationGranularity=${viewConfig.granularity}`
      ],
      [FILTER_NAME_TO_KPI_FILTER['customerSegmentationSections']]: [
        `customerSegmentationSections=${viewConfig.sections}`
      ],
      [FILTER_NAME_TO_KPI_FILTER['consostyleMicro']]: normalizedConsostyleMicroFilters.value,
      [FILTER_NAME_TO_KPI_FILTER['lifestageMicro']]: normalizedLifestageMicroFilters.value,
      [isInternalOrganization
        ? FILTER_NAME_TO_KPI_FILTER['businessBrand']
        : FILTER_NAME_TO_KPI_FILTER['businessBrandReduced']]: filters.value.businessBrandFilters
    };
  });
};
